// ──────────────────────────────────────────────────────────────────────────
// designTokens.js — the visual vocabulary of Gilda.
//
// Every named constant here represents a deliberate design choice that
// should look the SAME across the mockup (receipt_wizard_mockup.html)
// and the production app (ReceiptsTab.jsx, InvoicesTab.jsx, etc.).
//
// Why this file exists: pre-2026-06-28, color values + spacing + border
// styles were inlined directly into every component file. The same
// "#D4007A" fuchsia accent appeared ~30 different times across the
// codebase. Drift was inevitable — a copy-paste typo into "#D5007A"
// would land at one site and slowly erode the design system across
// commits. With tokens, every reference points to one source of truth.
// Change a value here, every consumer updates simultaneously.
//
// Reading order:
//   1. INK colors — text colors, from primary to muted
//   2. SURFACE + LINE — backgrounds + borders
//   3. ZONE — status/zone accents (Specifying / Quoting / Committed / Cancelled)
//   4. ALERT — warning/error/success states
//   5. SPACE — the spacing scale (xs through xxl)
//   6. RADIUS — corner radii
//   7. BORDER — pre-composed hairline strings
//   8. TYPE — pre-composed font + size + weight bundles
//   9. SHADOW — drop shadows + scrim
//  10. FEATURE — font-feature-settings strings (tabular numerals, etc.)
//
// Design discipline reminder (Bauhaus per CLAUDE.md):
//   • White ground (#FFFFFF). NO semitransparent fills.
//   • Hairline borders (0.5px) for separation. NEVER tinted backgrounds.
//   • Color used sparingly — INK for text, ZONE for status accents.
//   • DM Sans typeface. Uppercase eyebrows at 10px, body at 11pt.
//   • Tabular numerals on every numeric display.
//
// If you find yourself adding an inline color or border in a component,
// add it here first and import the token. The mockup file should be the
// design contract, not just a reference.
// ──────────────────────────────────────────────────────────────────────────

// ── 1. INK ───────────────────────────────────────────────────────────────
// Text colors. INK_1 is the loudest; INK_3 is the quietest reading.
// INK_4 is the placeholder color — should not be used for any real text.

export var INK_1 = '#111111';   // primary text (item names, totals, headings)
export var INK_2 = '#555552';   // secondary text (project labels, dates, body copy)
export var INK_3 = '#888880';   // muted text (eyebrows, hints, "matches estimate" affirmations)
export var INK_4 = '#BBBBBB';   // placeholder text (empty-state italics, "none yet")

// ── 2. SURFACE + LINE ────────────────────────────────────────────────────

export var SURFACE = '#FFFFFF'; // main background — every panel sits on white.

// GROUND_OFF is a deliberate off-white surface for low-attention background
// regions — the receipts list page ground, hover states on list rows, and
// any "secondary canvas" area where the eye should pass through to the
// content above it. Sanctioned 2026-06-28 (Cimon) as the SECOND allowed
// non-white surface, alongside ALERT_INFO_BG. Do not invent additional
// tinted backgrounds. If you reach for a third, ask first.
export var GROUND_OFF = '#FAFAF8';

// Hairline colors — three weights from lightest (within-card) to darkest
// (page-shell separators).
export var LINE_ROW       = '#EFEFEC';  // row separators inside tables/cards
export var LINE_CONTAINER = '#E0E0DC';  // outer card borders
export var LINE_SHELL     = '#D8D7D2';  // page-shell separators (toolbar, modal frame)

// ── 3. ZONE — the three-zone status palette ──────────────────────────────
// Items move through three zones: Specifying (yellow), Quoting (fuchsia),
// Committed (green). Cancelled is the terminal grey. Per CLAUDE.md the
// dots ARE the status indicator — never use background fills to convey
// status.

export var ZONE_YELLOW  = '#F5C800';  // Specifying zone (item is in flux)
export var ZONE_FUCHSIA = '#D4007A';  // Quoting zone + project accent + Ready-to-invoice + receipt selection
export var ZONE_GREEN   = '#1A5C38';  // Committed zone + success + PAID IN FULL
export var ZONE_GREY    = '#888880';  // Cancelled (terminal) — same as INK_3 but named for status semantics

// ── 4. ALERT — warning/error/success state colors ────────────────────────
// Pre-Bauhaus we used tinted backgrounds for alerts (#FDECEA red wash).
// Post-Bauhaus, alerts are colored TEXT + colored hairline borders on
// a white ground. The TEXT colors are the canonical alert hues; the
// BORDER colors are the matched accent for the hairline.

export var ALERT_RED_TEXT     = '#C8322A';  // destructive / blocker copy
export var ALERT_RED_BORDER   = '#C8322A';  // matched border (full saturation; intentional contrast)
export var ALERT_AMBER_TEXT   = '#665518';  // warning / "heads up" copy
export var ALERT_AMBER_BORDER = '#C9A227';  // matched border
export var ALERT_INFO_BG      = '#FFF0F8';  // VERY rare exception — the only tint allowed,
                                            // reserved for the receipt wizard's "client visibility"
                                            // callout (the one field that goes live to the client).
                                            // Use sparingly. If you reach for this, ask first.

// ── 5. SPACE — the spacing scale ─────────────────────────────────────────
// Powers-of-2-ish progression. Use in pixels. Don't invent intermediate
// values; if you need 9px, you're probably wrong about something.


// ── 6. RADIUS — corner radii ─────────────────────────────────────────────
// Pills (status badges, kind cards) have larger radii; everything else
// has small. No fully-round elements (no avatars, no perfect circles
// except status dots).


// ── 7. BORDER — pre-composed hairline strings ────────────────────────────
// CSS shorthand strings ready for `border` / `border-bottom` / etc.
//
// Two weight families:
//
//   HAIRLINE_* (0.5px) — the default. Row separators, internal divisions,
//   alert borders. Quiet. Use these unless there's a reason not to.
//
//   HAIRLINE_1PX_* + HAIRLINE_THICK_* — heavier weights for container
//   outlines and selected/active state cues, sanctioned 2026-06-28
//   (Cimon: "use the mockup"). The receipt wizard mockup uses 1px for
//   most container outlines and 1.5px to indicate selected/active state
//   on Kind cards and filter chips. Without these, selection drawn at
//   0.5px is too quiet to read at glance.

export var HAIRLINE         = '0.5px solid ' + LINE_CONTAINER;  // outer card border
export var HAIRLINE_ROW     = '0.5px solid ' + LINE_ROW;        // row separator
export var HAIRLINE_SHELL   = '0.5px solid ' + LINE_SHELL;      // page chrome
export var HAIRLINE_FUCHSIA = '0.5px solid ' + ZONE_FUCHSIA;    // selected card / Ready-to-invoice pill
export var HAIRLINE_RED     = '0.5px solid ' + ALERT_RED_BORDER;
export var HAIRLINE_AMBER   = '0.5px solid ' + ALERT_AMBER_BORDER;

// 1px container weight — used for outer outlines on cards / modals /
// drop strips / kind cards (unselected). Reads as a deliberate frame
// without crossing into "heavy."
export var HAIRLINE_1PX           = '1px solid ' + LINE_CONTAINER;
export var HAIRLINE_1PX_SHELL     = '1px solid ' + LINE_SHELL;

// 1.5px selected/active weight — used to indicate selection on Kind
// cards (fuchsia for project kinds, ink for non-project) and on active
// filter chips (ink). The width change is the affordance signal.
export var HAIRLINE_THICK_INK     = '1.5px solid ' + INK_1;
export var HAIRLINE_THICK_FUCHSIA = '1.5px solid ' + ZONE_FUCHSIA;

export var DASHED_DROP      = '1.5px dashed ' + LINE_SHELL;     // upload drop zone
export var ACCENT_LEFT      = '2px solid ' + ZONE_FUCHSIA;      // selected row left edge

// ── 8. TYPE — pre-composed font + size + weight bundles ──────────────────
// Spread as inline styles via Object.assign. Each bundle captures a
// distinct semantic role; using them by name keeps text hierarchy
// consistent across components.

export var FONT_FAMILY = "'DM Sans', sans-serif";

// Eyebrow — uppercase, letter-spaced, small + muted.
// Used: "RECEIPT PDF" label, "INBOX (4)" section headers, "STEP 1 OF 4".
export var TYPE_EYEBROW = {
  fontFamily: FONT_FAMILY,
  fontSize: 10,
  fontWeight: 500,
  color: INK_3,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
};


// ── 9. SHADOW + SCRIM ────────────────────────────────────────────────────
// Drop shadows are reserved for elevation moments — modals, drag overlays.
// Body content never floats; cards sit flat on the surface with hairlines.

export var SHADOW_MODAL    = '0 8px 40px rgba(0,0,0,0.3)';   // receipt detail modal lift
export var SCRIM           = 'rgba(0,0,0,0.55)';             // modal backdrop
export var DRAG_OVERLAY_BG = 'rgba(17,17,17,0.78)';          // full-screen drag overlay

// ── 10. FEATURE — font-feature-settings strings ──────────────────────────
// Apply via `fontFeatureSettings: TABULAR` or spread into Object.assign.

export var TABULAR = '"tnum", "lnum"';  // tabular + lining numerals — apply to every $ amount

// ── 11. Z_INDEX — layering constants ─────────────────────────────────────
// Avoid magic z-index numbers. Use these.

export var Z_MODAL          = 1000;

// ── 12. RECEIPT KIND ACCENT — the project-related fuchsia rule ───────────
// Project-related receipt kinds (vendor_*, project_reimbursable) wear the
// fuchsia accent on selection states (Kind card border, etc.). Non-project
// kinds (personal/office) wear the plain INK_1 black accent. Use this
// helper at the consumer site so the rule lives in one place.

